import { offline, requireConnection } from './connectivity';
// Adjust these initial values after observing real reading behaviour; never write per scroll event.
export const READING_POLICY = { debounceMs: 1200, minSaveMs: 5000, maxWaitMs: 10000 };
export class ReadingSession {
    public onUnauthorized: (token: string) => void = () => {};
    public bookmarks: any[] = [];
    public position: any = null;
    public message = '';
    public ready = false;
    public busy = false;
    public pending: any = null;
    private id = '';
    private token = '';
    private ownerKey = '';
    private epoch = 0;
    private activity = 0;
    private startedActivity = 0;
    private intentUntil = 0;
    private userScrolled = false;
    private sources: any[] = [];
    private sourceMap = new Map<string, any>();
    private anchors: HTMLElement[] = [];
    private dirty: any = null;
    private lastSave = 0;
    private firstDirty = 0;
    private timer: any;
    private sampleTimer: any;
    private changed: () => void = () => {};
    private reader: any;
    private supported = false;
    private disposed = false;
    private initializing = false;
    private positionVersion = 0;
    private interaction = (event: Event) => {
        if (event instanceof KeyboardEvent && !['ArrowDown','ArrowUp','PageDown','PageUp','Home','End',' '].includes(event.key)) return;
        if (event.target instanceof HTMLElement && event.target.closest('input,textarea,select')) return;
        this.activity++;
        this.intentUntil = !(event instanceof PointerEvent) || event.clientX >= document.documentElement.clientWidth ? Date.now() + 1500 : 0;
    };
    private scrolled = () => {
        if (Date.now() > this.intentUntil || !this.id) return;
        this.userScrolled = true;
        this.activity++; this.intentUntil = Date.now() + 1500;
        if (location.hash) history.replaceState(history.state, '', location.pathname + location.search);
        if (!this.supported || !this.ready) return;
        if (!this.sampleTimer) this.sampleTimer = setTimeout(() => { this.sampleTimer = null; this.capture(); }, 150);
    };
    private leaving = () => { if (this.sampleTimer) { clearTimeout(this.sampleTimer); this.sampleTimer = null; this.capture(true); } void this.flush(true); };
    private hidden = () => { if (document.visibilityState === 'hidden') this.leaving(); };
    private online = () => {
        if (this.pending || this.dirty) { this.message = '연결이 복구되었습니다. 미확인 저장은 같은 요청 다시 확인을 눌러 주세요.'; this.changed(); }
        if (!this.ready) void this.load(false);
    };
    constructor() {
        for (const name of ['wheel','touchstart','touchmove','pointerdown','keydown']) window.addEventListener(name, this.interaction, { passive: true });
        window.addEventListener('scroll', this.scrolled, { passive: true });
        window.addEventListener('pagehide', this.leaving);
        document.addEventListener('visibilitychange', this.hidden);
        window.addEventListener('online', this.online);
    }
    route(id: string, token: string, changed: () => void, reader: any) {
        this.changed = changed; this.reader = reader;
        if (id === this.id && token === this.token) return;
        if (token && token === this.token) this.leaving();
        this.epoch++; clearTimeout(this.timer); clearTimeout(this.sampleTimer); this.sampleTimer = null;
        this.id = id; this.token = token; this.startedActivity = this.activity;
        this.ready = false; this.initializing = false; this.busy = false; this.pending = null; this.position = null;
        this.positionVersion = 0; this.ownerKey = ''; this.bookmarks = []; this.message = ''; this.dirty = null; this.firstDirty = 0;
        this.sources = []; this.sourceMap.clear(); this.anchors = []; this.supported = false; this.intentUntil = 0; this.userScrolled = false;
        if (!id && token) void this.load();
    }
    configure(run: any) {
        if (!run || run.id !== this.id) { this.sources = []; this.anchors = []; this.supported = false; return; }
        this.supported = ['F1','F2'].includes(run.input.feature_id) && !run.question;
        this.sources = run.question_sources || [];
        this.sourceMap = new Map(this.sources.map(s => [s.anchor, s]));
        this.anchors = Array.from(document.querySelectorAll<HTMLElement>('[data-reader-anchor]')).filter(el => this.sourceMap.has(el.id));
        if (this.supported && !this.ready && !this.initializing && this.token) void this.load();
        // F3 still supports explicit question links, but has no automatic reading record.
        if (!this.supported && location.hash && !this.initializing) { this.initializing = true; void this.resumeAnchor(location.hash.slice(1), 0, true); }
    }
    private async request(path: string, body?: any, keepalive = false, token = this.token) {
        requireConnection();
        const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
        try {
            const response = await fetch('/api/saju/runs' + path, {signal: controller.signal, method: body ? 'POST' : 'GET', cache: 'no-store', credentials: 'include', keepalive,
                headers: {Authorization: 'Bearer ' + token, ...(body ? {'Content-Type':'application/json'} : {})}, body: body ? JSON.stringify(body) : undefined});
            const value = await response.json();
            if ([401,403].includes(response.status) && token === this.token) this.onUnauthorized(token);
            if (!response.ok) throw {status: response.status, code: value.error_code};
            return value.data;
        } finally { clearTimeout(timeout); }
    }
    async load(restore = true) {
        if (!this.token || this.initializing) return;
        const epoch = this.epoch; this.initializing = true;
        try {
            const value = await this.request(this.id ? '/' + this.id + '/reading-state' : '/reading-records');
            if (epoch !== this.epoch || this.disposed) return;
            this.bookmarks = value.bookmarks; this.position = value.position; this.positionVersion = value.position_version || this.position?.version || 0;
            this.ownerKey = value.owner_key; this.ready = true;
            if (!this.pending) {
                try { this.pending = JSON.parse(sessionStorage.getItem(this.storageKey()) || 'null'); } catch {}
                if (this.pending) this.message = '저장 확인이 끝나지 않은 문단이 있습니다. 같은 요청 다시 확인을 눌러 주세요.';
            }
            this.changed();
            if (restore && this.id && this.activity === this.startedActivity) {
                const anchor = location.hash.slice(1) || (!this.position?.unavailable && this.position?.anchor);
                if (anchor) await this.resumeAnchor(anchor, location.hash ? 0 : this.position.relative, true);
            } else if (restore && this.userScrolled) this.capture();
        } catch { if (epoch === this.epoch) this.message = '읽기 기록을 불러오지 못했습니다. 본문은 계속 읽을 수 있습니다. 연결 후 다시 확인해 주세요.'; }
        finally { if (epoch === this.epoch) { this.initializing = false; this.changed(); } }
    }
    private storageKey() { return 'saju.reading.' + this.ownerKey + '.' + (this.id || 'library'); }
    private remember() {
        if (!this.ownerKey) return;
        try { if (this.pending) sessionStorage.setItem(this.storageKey(), JSON.stringify(this.pending)); else sessionStorage.removeItem(this.storageKey()); } catch {}
    }
    activeBookmarks() { return this.bookmarks.filter(b => b.active); }
    get unsent() { return !!(this.pending || this.dirty); }
    async confirmSave() { if (this.pending) await this.retry(); else await this.flush(); }
    saved(anchor: string) { return this.bookmarks.some(b => b.active && b.anchor === anchor && b.run_id === this.id); }
    async toggle(anchor: string) {
        if (!this.ready || this.busy || this.pending) return;
        const source = this.sourceMap.get(anchor); if (!source) return;
        const id = 'bookmark:' + source.id, previous = this.bookmarks.find(b => b.id === id);
        this.pending = {kind:'bookmark', id, reference:source.reference, active:!previous?.active,
            expected_version:previous?.version || 0, operation_id:crypto.randomUUID()};
        this.remember(); await this.retry();
    }
    async clearPosition() {
        if (!this.position || this.busy || this.pending) return;
        this.pending = {kind:'position', active:false, expected_version:this.positionVersion, operation_id:crypto.randomUUID()};
        this.remember(); await this.retry();
    }
    async remove(record: any) {
        if (!this.ready || this.busy || this.pending) return;
        this.pending = {kind:'bookmark', id:record.id, run_id:record.run_id, active:false,
            expected_version:record.version, operation_id:crypto.randomUUID()};
        this.remember(); await this.retry();
    }
    async retry(keepalive = false) {
        if (!this.pending || this.busy) return;
        const epoch = this.epoch, pending = this.pending; this.busy = true; this.changed();
        try {
            const path = pending.run_id ? '/reading-records' : '/' + this.id + '/reading-state';
            const record = await this.request(path, pending, keepalive);
            if (epoch !== this.epoch || this.disposed) return;
            if (record.kind === 'position') { this.position = record.active ? record : null; this.positionVersion = record.version; }
            else this.bookmarks = [...this.bookmarks.filter(b => b.id !== record.id),record];
            this.pending = null; this.remember(); this.message = '읽기 기록이 저장되었습니다.';
        } catch (error: any) {
            if (epoch !== this.epoch || this.disposed) return;
            if ([409,404,401,403,422].includes(error.status)) {
                this.pending = null; this.remember(); this.dirty = null;
                if (error.status === 409) { await this.load(false); this.message = '다른 탭의 최신 기록 또는 원문 변경을 확인했습니다. 이번 저장은 반영되지 않았습니다. 화면은 이동하지 않습니다.'; }
                else { this.ready = false; this.message = '원문 또는 열람 권한을 확인할 수 없어 저장하지 못했습니다.'; }
            } else this.message = '서버 저장을 확인하지 못했습니다. 본문은 계속 읽을 수 있습니다. 같은 요청 다시 확인을 눌러 주세요.';
        } finally { if (epoch === this.epoch) { this.busy = false; this.changed(); if (this.dirty && !this.pending) { if (document.visibilityState === 'hidden') void this.flush(true); else this.schedule(); } } }
    }
    capture(leaving = false) {
        if (offline()) return;
        if (!this.ready || !this.supported || (!leaving && document.visibilityState === 'hidden')) return;
        const line = Math.min(120, innerHeight * .2);
        const visible = this.anchors.map(el => ({el,rect:el.getBoundingClientRect()})).filter(x => x.rect.height > 0 && x.rect.bottom > line && x.rect.top < innerHeight);
        const hit = visible.find(x => x.rect.top <= line) || visible[0];
        if (!hit) return; // A collapsed chapter, toolbar or blank page is not a new reading position.
        const source = this.sourceMap.get(hit.el.id);
        const relative = Math.max(0,Math.min(1,(line-hit.rect.top)/hit.rect.height));
        if (this.pending?.kind !== 'position' && this.position?.anchor === source.anchor && Math.abs(this.position.relative - relative) < .015) {
            this.dirty = null; this.firstDirty = 0; clearTimeout(this.timer); return;
        }
        this.dirty = {reference:source.reference, relative}; this.firstDirty ||= Date.now(); this.schedule();
    }
    private schedule() {
        clearTimeout(this.timer);
        const now = Date.now(), delay = Math.max(this.lastSave + READING_POLICY.minSaveMs - now,
            Math.min(READING_POLICY.debounceMs, this.firstDirty + READING_POLICY.maxWaitMs - now), 0);
        this.timer = setTimeout(() => void this.flush(), delay);
    }
    async flush(keepalive = false) {
        clearTimeout(this.timer);
        if (offline()) return;
        if (!this.dirty || this.pending || this.busy || !this.ready) return;
        this.pending = {kind:'position',active:true,expected_version:this.positionVersion,operation_id:crypto.randomUUID(),...this.dirty};
        this.dirty = null; this.firstDirty = 0; this.lastSave = Date.now(); this.remember(); await this.retry(keepalive);
    }
    async resumeAnchor(anchor: string, relative = 0, automatic = false) {
        const epoch = this.epoch, activity = this.activity;
        if (automatic && activity !== this.startedActivity) return;
        this.intentUntil = 0;
        await this.reader.stableGo(anchor, () => epoch !== this.epoch || activity !== this.activity || this.disposed, relative);
        if (epoch === this.epoch) this.changed();
    }
    async resume() { if (this.position && !this.position.unavailable) await this.resumeAnchor(this.position.anchor, this.position.relative); }
    destroy() {
        this.leaving(); this.disposed = true; this.epoch++; clearTimeout(this.timer); clearTimeout(this.sampleTimer);
        for (const name of ['wheel','touchstart','touchmove','pointerdown','keydown']) window.removeEventListener(name, this.interaction);
        window.removeEventListener('scroll',this.scrolled); window.removeEventListener('pagehide',this.leaving);
        document.removeEventListener('visibilitychange',this.hidden); window.removeEventListener('online',this.online);
    }
}
